import './LastSearch.scss';

import cn from 'classnames';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import Button from '@/common/components/atoms/Button/Button';
import ScrollList from '@/common/components/molecules/ScrollList/ScrollList';
import {
  PRODUCTS_LAST_VIEWED_LIMIT,
  ROUTE_SEARCH,
  SEARCH_LATEST_LIMIT,
  SOURCE,
} from '@/common/constants';
import { useSearchContext } from '@/common/contexts/SearchContext';
import useProductsLastViewed from '@/common/hooks/products/useProductsLastViewed';
import useSearchLatest from '@/common/hooks/search/useSearchLatest';
import Product from '@/ebat/components/blocks/Common/Product/Product';
import { TAG_MANAGER } from '@/ebat/constants';
import {
  MENU_NAME_SEARCH,
  MENU_TYPE_EXTERNAL,
  useHeaderMenuContext,
} from '@/ebat/contexts/Header/HeaderMenuContext/HeaderMenuContext';

import {
  LastSearchDefaultProps,
  LastSearchPropTypes,
} from './LastSearch.proptypes';

const LastSearch = ({ className, title, isOpen }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { setSearchParams } = useSearchContext();
  const {
    isOpen: isHeaderMenuOpen,
    handleClose: handleHeaderMenuClose,
  } = useHeaderMenuContext();
  const isSearchOpen = isHeaderMenuOpen({
    name: MENU_NAME_SEARCH,
    type: MENU_TYPE_EXTERNAL,
  });
  const isSearchPage = location.pathname.includes(ROUTE_SEARCH);
  const innerIsOpen = isOpen || (isSearchOpen && !isSearchPage);
  const nodeRef = useRef(null);

  const { data: search, isSuccess: isSuccessSearch } = useSearchLatest({
    limit: SEARCH_LATEST_LIMIT,
  });

  const {
    data: products,
    isSuccess: isSuccessProducts,
  } = useProductsLastViewed({
    limit: PRODUCTS_LAST_VIEWED_LIMIT,
  });

  /**
   * Select term and set new search term
   *
   * @param {event} e - DOM event
   * @param {string} newTerm - New term for search
   */
  const selectTerm = (newTerm) => {
    setSearchParams(newTerm);
    handleHeaderMenuClose();
  };

  const handleOnClose = () => {
    handleHeaderMenuClose();
  };

  return (
    <CSSTransition
      in={innerIsOpen}
      timeout={300} // CSS transition
      classNames="last-search"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div
        className={cn('last-search', className)}
        data-qa-id="last-search-content"
        ref={nodeRef}
      >
        <div className="last-search__wrapper">
          <div className="last-search__terms">
            <span className="last-search__title">
              {t('common:messages:labels.latest_searches')}
            </span>
            {isSuccessSearch &&
              (search.length > 0 ? (
                <ScrollList className="last-search__terms-list">
                  {search.map((term, index) => (
                    <li
                      className="last-search__term scroll-list__item"
                      key={`term-${term}-${index}`}
                    >
                      <Button
                        className="last-search__term-button"
                        context="last-search-select"
                        onClick={() => selectTerm(term)}
                      >
                        {term}
                      </Button>
                    </li>
                  ))}
                </ScrollList>
              ) : (
                <p className="last-search__out-a-search">
                  {t('common:messages:labels.out_a_search')}
                </p>
              ))}
          </div>
          <div className="last-search__products">
            {title && (
              <h1 className="h1" data-qa-id="title-h1">
                {title}
              </h1>
            )}
            <span className="last-search__title">
              {t('common:messages:labels.last_viewed')}
            </span>
            {isSuccessProducts && products?.data?.length > 0 && (
              <ul
                className="last-search__products-list"
                data-qa-id="last-search-products"
              >
                {products.data.map((product, i) => (
                  <li
                    className="last-search__products-item"
                    data-qa-id="last-search-products-item"
                    key={`product-${product.id}-${i}`}
                  >
                    <Product
                      addToCartOptions={{
                        dataLayer: { origin: TAG_MANAGER.ORIGINS.LAST_VIEWED },
                      }}
                      brand={product.brand}
                      codeOldCompanySku={product.code_old_company_sku}
                      companyAvailability={product.company_availability}
                      configurations={product.configurations}
                      configurator={product.configurator}
                      customerPrice={
                        product.price_and_stock?.price?.customer_price
                      }
                      discountPrice={
                        product.price_and_stock?.price?.discount_price
                      }
                      exclusivity={product.exclusivity}
                      flags={product.flags}
                      hasPrice={product.price_and_stock?.has_price}
                      hasStock={product.price_and_stock?.has_stock}
                      id={product.id}
                      image={product.image}
                      invoiceUnit={product.price_and_stock?.invoice_unit}
                      isFavorite={product.is_favorite}
                      mainSku={product.main_sku}
                      manufacturerSku={product.manufacturer_sku}
                      name={product.name}
                      onClickCallback={handleOnClose}
                      path={product.path}
                      publicPrice={product.price_and_stock?.price?.public_price}
                      saleUnit={product.price_and_stock?.sale_unit}
                      salesPackaging={product.price_and_stock?.sales_packaging}
                      sku={product.sku}
                      slug={product.slug}
                      source={SOURCE.LAST_SEEN}
                      splitPackaging={product.price_and_stock?.split_packaging}
                      stockStatus={product.price_and_stock?.stock_status}
                      stocks={product.price_and_stock?.stocks}
                      weeeAmount={product.price_and_stock?.price?.weee_amount}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

LastSearch.defaultProps = {
  ...LastSearchDefaultProps,
};

LastSearch.propTypes = {
  ...LastSearchPropTypes,
};

export default LastSearch;
